import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import TablePagination from '@mui/material/TablePagination';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { useDispatch } from 'react-redux';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useRef, useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import NamedAutocomplete from '../common/NamedAutocomplete';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import { Download, FilterAlt } from '@mui/icons-material';
import { Fab } from '@mui/material';
import { ArrowRightIcon } from '@mui/x-date-pickers';
import * as XLSX from "xlsx";

function Row(props) {
  const dispatch = useDispatch();
  const { row } = props;

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell align="center" component="th" scope="row">
          {row.id}
        </TableCell>
        <TableCell align="center">{row.vendor}</TableCell>
        <TableCell align="center">{row.invoiceNo}</TableCell>
        <TableCell align="center">{row.createdOn}</TableCell>
        <TableCell align="center">{row.grossAmount}</TableCell>
        <TableCell align="center">{row.tds}</TableCell>
        <TableCell align="center">{row.advance}</TableCell>
        <TableCell align="center">{row.hold}</TableCell>
        <TableCell align="center">{row.netPayable}</TableCell>
        <TableCell align="center">{row.remarks}</TableCell>
        <TableCell align="center">{row.location}</TableCell>
        <TableCell align="center">{row.user}</TableCell>
        <TableCell align="center">{row.checker}</TableCell>
        <TableCell align="center">{row.checkedOn}</TableCell>
        <TableCell align="center">{row.authorizer}</TableCell>
        <TableCell align="center">{row.authorizedOn}</TableCell>
        <TableCell align="center">{row.rejecter}</TableCell>
        <TableCell align="center">{row.rejectedOn}</TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    vendor: PropTypes.string.isRequired,
    grossAmount: PropTypes.number.isRequired,
    netPayable: PropTypes.number.isRequired,
    remarks: PropTypes.string.isRequired,
    user: PropTypes.string.isRequired,
    // issuedby: PropTypes.string.isRequired,
    // recievedby: PropTypes.string.isRequired,
  }).isRequired,
};

//copied from sorting and selecting
function descendingComparatorNumeric(a, b, orderBy) {

  var n = (b[orderBy] ?? "0")
  var m = (a[orderBy] ?? "0")

  if (typeof n === 'string')
    n = n.match(/[+-]?\d+(\.\d+)?/g).join("");

  if (typeof m === 'string')
    m = m.match(/[+-]?\d+(\.\d+)?/g).join("");

  if (n < m) {
    return -1;
  }
  if (n > m) {
    return 1;
  }
  return 0;
}

//copied from sorting and selecting
function descendingComparator(a, b, orderBy) {

  if (b[orderBy] ?? "" < a[orderBy] ?? "") {
    return -1;
  }
  if (b[orderBy] ?? "" > a[orderBy] ?? "") {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy, isNumeric) {
  return order === 'desc'
    ? (a, b) => isNumeric ? descendingComparatorNumeric(a, b, orderBy) : descendingComparator(a, b, orderBy)
    : (a, b) => isNumeric ? -descendingComparatorNumeric(a, b, orderBy) : -descendingComparator(a, b, orderBy);
}

const headCells = [
  {
    id: 'id',
    numeric: true,
    disablePadding: false,
    label: 'ID',
  },
  {
    id: 'vendor',
    numeric: false,
    disablePadding: false,
    label: 'Vendor',
  },
  {
    id: 'invoiceNo',
    numeric: false,
    disablePadding: false,
    label: 'Invoice No',
  },
  {
    id: 'createdOn',
    numeric: false,
    disablePadding: false,
    label: 'Requested On',
  },
  {
    id: 'grossAmount',
    numeric: true,
    disablePadding: false,
    label: 'Gross Amount',
  },
  {
    id: 'tds',
    numeric: true,
    disablePadding: false,
    label: 'TDS',
  },
  {
    id: 'advance',
    numeric: true,
    disablePadding: false,
    label: 'Advance',
  },
  {
    id: 'hold',
    numeric: true,
    disablePadding: false,
    label: 'Hold',
  },
  {
    id: 'netPayable',
    numeric: true,
    disablePadding: false,
    label: 'Net Payable',
  },
  {
    id: 'remarks',
    numeric: false,
    disablePadding: false,
    label: 'Remarks',
  },
  {
    id: 'location',
    numeric: false,
    disablePadding: false,
    label: 'Location',
  },
  {
    id: 'user',
    numeric: false,
    disablePadding: false,
    label: 'Maker',
  },
  {
    id: 'checker',
    numeric: false,
    disablePadding: false,
    label: 'Checker',
  },
  {
    id: 'checkedOn',
    numeric: false,
    disablePadding: false,
    label: 'Checked On',
  },
  {
    id: 'authorizer',
    numeric: false,
    disablePadding: false,
    label: 'Authorizer',
  },
  {
    id: 'authorizedOn',
    numeric: false,
    disablePadding: false,
    label: 'Authorized On',
  },
  {
    id: 'rejecter',
    numeric: false,
    disablePadding: false,
    label: 'Rejecter',
  },
  {
    id: 'rejectedOn',
    numeric: false,
    disablePadding: false,
    label: 'Rejected On',
  },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow className='info'>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            //align='center'
            align={headCell.numeric ? 'center' : 'center'}
            //text-align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'desc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function FilterDrawer(props) {
  const { setFilterSwitch } = props;
  const { paymentStatusList } = props;
  const { locationList } = props;
  const { setPaymentStatus } = props;
  const { setLocation } = props;

  const { setReqSDate } = props;
  const { setReqEDate } = props;

  const { setCheSDate } = props;
  const { setCheEDate } = props;

  const { setAuthSDate } = props;
  const { setAuthEDate } = props;

  const { setRejSDate } = props;
  const { setRejEDate } = props;

  const [open, setOpen] = useState(false);

  const [vendor, setVendor] = useState('');

  const [reqStartDate, setReqStartDate] = useState(null);
  const [reqEndDate, setReqEndDate] = useState(null);

  const [checkStartDate, setCheckStartDate] = useState(null);
  const [checkEndDate, setCheckEndDate] = useState(null);

  const [authStartDate, setAuthStartDate] = useState(null);
  const [authEndDate, setAuthEndDate] = useState(null);

  const [rejStartDate, setRejStartDate] = useState(null);
  const [rejEndDate, setRejEndDate] = useState(null);

  const statusAutocompleteElement = useRef('');
  const locationAutocompleteElement = useRef('');

  useEffect(() => {
    setReqSDate(reqStartDate)
  }, [reqStartDate])

  useEffect(() => {
    setReqEDate(reqEndDate)
  }, [reqEndDate])

  useEffect(() => {
    setCheSDate(checkStartDate)
  }, [checkStartDate])

  useEffect(() => {
    setCheEDate(checkEndDate)
  }, [checkEndDate])

  useEffect(() => {
    setAuthSDate(authStartDate)
  }, [authStartDate])

  useEffect(() => {
    setAuthEDate(authEndDate)
  }, [authEndDate])

  useEffect(() => {
    setRejSDate(rejStartDate)
  }, [rejStartDate])

  useEffect(() => {
    setRejEDate(rejEndDate)
  }, [rejEndDate])

  const handleFilter = (event) => {
    //console.log(`In handleFilter : ${event.target.value}`);
    //rows = rows.filter(row => row.department.includes(event.target.value));
    setVendor(event.target.value);
    setFilterSwitch(event.target.value);
    //console.log(`rows count : ${rows.length}`)              
  };

  const updateStatusSelection = (item) => {
    setPaymentStatus(item.name);
  };

  const updateLocationSelection = (item) => {
    setLocation(item.name);
  };

  const clearFilter = () => {
    setVendor("");
    setFilterSwitch("");
    setReqStartDate(null);
    setReqEndDate(null);
    setCheckStartDate(null);
    setCheckEndDate(null);
    setAuthStartDate(null);
    setAuthEndDate(null);
    setRejStartDate(null);
    setRejEndDate(null);

    statusAutocompleteElement.current.resetState();
    locationAutocompleteElement.current.resetState();

    setPaymentStatus("");
    setLocation("");

    setOpen(false);
  }

  {/* onClick={toggleDrawer(false)} */ }
  const DrawerList = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: 400, maxWidth: 'calc(100vw - 1.7rem)' }} p={2} role="presentation">

        <TextField value={vendor} style={{ width: '100%' }} id="deptFilter" label="Vendor" variant="outlined" onChange={handleFilter} />

        <Typography
          edge="start"
          m={0.5}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          Requsted:
        </Typography>
        <div style={{ display: 'flex' }}>
          <DatePicker sx={{ width: '100%' }} label="Start" format="DD/MM/YYYY" value={reqStartDate} onChange={setReqStartDate} />
          <DatePicker sx={{ width: '100%' }} label="End" format="DD/MM/YYYY" value={reqEndDate} onChange={setReqEndDate} />
        </div>

        <Typography
          edge="start"
          m={0.5}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          Checked:
        </Typography>
        <div style={{ display: 'flex' }}>
          <DatePicker sx={{ width: '100%' }} label="Start" format="DD/MM/YYYY" value={checkStartDate} onChange={setCheckStartDate} />
          <DatePicker sx={{ width: '100%' }} label="End" format="DD/MM/YYYY" value={checkEndDate} onChange={setCheckEndDate} />
        </div>

        <Typography
          edge="start"
          m={0.5}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          Authorized:
        </Typography>
        <div style={{ display: 'flex' }}>
          <DatePicker sx={{ width: '100%' }} label="Start" format="DD/MM/YYYY" value={authStartDate} onChange={setAuthStartDate} />
          <DatePicker sx={{ width: '100%' }} label="End" format="DD/MM/YYYY" value={authEndDate} onChange={setAuthEndDate} />
        </div>

        <Typography
          edge="start"
          m={0.5}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          Rejected:
        </Typography>
        <div style={{ display: 'flex' }}>
          <DatePicker sx={{ width: '100%' }} label="Start" format="DD/MM/YYYY" value={rejStartDate} onChange={setRejStartDate} />
          <DatePicker sx={{ width: '100%' }} label="End" format="DD/MM/YYYY" value={rejEndDate} onChange={setRejEndDate} />
        </div>

        <Typography
          edge="start"
          m={0.5}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          Status:
        </Typography>
        <NamedAutocomplete suggestions={paymentStatusList} ref={statusAutocompleteElement} change={updateStatusSelection} />

        <Typography
          edge="start"
          m={0.5}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          Location:
        </Typography>
        <NamedAutocomplete suggestions={locationList} ref={locationAutocompleteElement} change={updateLocationSelection} />
        <hr />
        <Button style={{ width: '100%' }} variant="outlined" onClick={clearFilter}>Clear Filter</Button>
      </Box>
    </LocalizationProvider>
  );

  return (
    <div>
      {/* <Button variant="contained" style={{justifyContent: 'flex-end'}} onClick={toggleDrawer(true)}>Filter</Button> */}
      <Drawer variant='persistent' anchor='right' open={open}>
        {DrawerList}
      </Drawer>
      <Fab style={{
        position: 'fixed',
        bottom: '2rem',
        right: open ? 'calc(min(400px, 100vw - 1.9rem) - 1.7rem)' : '2rem', // Adjust 240px to the width of the drawer
        transition: 'right 0.2s ease',
        zIndex: 1300,
      }} onClick={() => { setOpen(!open) }} color="primary" aria-label="close">
        {open ? <ArrowRightIcon /> : <FilterAlt />}
      </Fab>
    </div>
  );
}
FilterDrawer.prototype = {
  setFilterSwitch: PropTypes.func,
  paymentStatusList: PropTypes.array,
  locationList: PropTypes.array,
  setPaymentStatus: PropTypes.func,
  setLocation: PropTypes.func,
  setReqSDate: PropTypes.func,
  setReqEDate: PropTypes.func,
  setCheSDate: PropTypes.func,
  setCheEDate: PropTypes.func,
  setAuthSDate: PropTypes.func,
  setAuthEDate: PropTypes.func,
  setRejSDate: PropTypes.func,
  setRejEDate: PropTypes.func,
};

function EnhancedTableToolbar(props) {
  const { numSelected, setFilterSwitch } = props;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {/* {numSelected > 0 ? (
          
        ) : (
          <Typography
            sx={{ flex: '1 1 100%' }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            Requests
          </Typography>
        )} */}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            {/* <DeleteIcon /> */}
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Download list">
          <IconButton >
            {/* <DownloadIcon/> */}
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  setFilterSwitch: PropTypes.func
};

//end copying from sorting and selecting

export default function CollapsibleTable() {

  // const approverRoles = ['Administrator'];
  // console.log('in collapsible table');
  const REQUEST_URL = "/PaymentApproval/GetPaymentRequestHistory";
  const REQUEST_VENDORS_URL = "/PaymentApproval/GetVendors";
  const { auth, setAuth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  //let rows = [];

  // need to fetch data from api
  const [rows, setPaymentRequestRows] = useState([]);
  const [vendors, setVendorRows] = useState([]);


  //setting data in redux store
  const dispatch = useDispatch();


  useEffect(() => {
    //let rows = [];
    //useSelector(() => store.getState().orderitem.ApprovalOrders);
    //const approvedOrders = useSelector(() => store.getState().order.approvedorders);
    // console.log(`Rows key length : ${Object.keys(rows).length}`);
    if (Object.keys(rows).length === 0) {
      //console.log('start of get');
      try {
        //console.log(`Bearer ${auth.accessToken}`)
        const response = axiosPrivate.get(REQUEST_URL, {
          withCredentials: true,
          headers: {
            'content-type': 'application/json',
            'Authorization': `Bearer ${auth?.accessToken}`
            //'access-control-allow-origin' : '*'
          }
        })
          .then(response => {
            //console.log(response.data);
            //console.log(`Response : ${JSON.stringify(response.data)}`);
            setPaymentRequestRows(response.data);
            //rows = response.data;
            //dispatch({ type: 'addOrders', payload: response.data  });
            //console.log(`Rows : ${JSON.stringify(rows)}`);
            setPaymentStatusList([{ name: "Any" }, { name: "Pending" }, { name: "Checked" }, { name: "Authorized" }, { name: "Rejected" }]);
            setLocationList([{ name: "Any" }, { name: "Changodar" }, { name: "Sanand" }, { name: "Ahmedabad" }]);
          })
          .catch(error => {
            console.log(error);
          });


        axiosPrivate.get(REQUEST_VENDORS_URL, {
          withCredentials: true,
          headers: {
            'content-type': 'application/json',
            'Authorization': `Bearer ${auth?.accessToken}`
            //'access-control-allow-origin' : '*'
          }
        })
          .then(response => {
            //console.log(response.data);
            //console.log(`Response : ${JSON.stringify(response.data)}`);
            setVendorRows(response.data);
            //rows = response.data;
            //dispatch({ type: 'addOrders', payload: response.data  });
            //console.log(`Rows : ${JSON.stringify(rows)}`);

          })
          .catch(error => {
            console.log(error);
          });

      }
      catch (err) {

        console.log(err.response);

      }

    }
  }, []);




  //console.log([...rows]);

  //start copy from sorting and selecting

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterSwitch, setFilterSwitch] = useState('');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paymentStatusList, setPaymentStatusList] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [locationList, setLocationList] = useState([]);
  const [location, setLocation] = useState("");

  const [reqStartDate, setReqStartDate] = useState(null);
  const [reqEndDate, setReqEndDate] = useState(null);
  const [checkStartDate, setCheckStartDate] = useState(null);
  const [checkEndDate, setCheckEndDate] = useState(null);
  const [authStartDate, setAuthStartDate] = useState(null);
  const [authEndDate, setAuthEndDate] = useState(null);
  const [rejStartDate, setRejStartDate] = useState(null);
  const [rejEndDate, setRejEndDate] = useState(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    //console.log('setOrder');
    setOrder(isAsc ? 'desc' : 'asc');
    //console.log('setOrderby', property);
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      //console.log('setSelected')
      setSelected(newSelected);
      return;
    }
    //console.log('setSelected empty')
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    //console.log('setSelected handleclick')
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    //console.log('setpage');
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    //console.log('setRowsPerPage');
    setRowsPerPage(parseInt(event.target.value, 10));
    //console.log('setpage 1');
    setPage(0);
  };

  const handleChangeDense = (event) => {
    //console.log('setdense');
    setDense(event.target.checked);
  };

  const excludedColumns = ['vid']; // Columns to exclude

  const preprocessData = (data, excludedColumns) => {
    return data.map(item => {
      const newItem = { ...item };
      excludedColumns.forEach(col => delete newItem[col]);
      return newItem;
    });
  };

  const handleDownload = async (e) => {
    let paymentHeading = [['Sr. No.', 'Date', 'Beneficiary Name', 'Gross Amount', 'TDS', 'Advance', 'Hold', 'Net Payable', 'Remarks/Narration', 'Location', 'Maker', 'Checker', 'Authorizer', 'Rejecter']];
    let vendorHeading = [['Beneficiary Name', 'Account No', 'IFSC', 'Bank Name', 'PAN', 'GSTIN', 'Phone', 'Email', 'Amount']];

    const paymentData = filteredRow.map((p) => ({ id: p.id, date: p.createdOn, vendor: p.vendor, grossAmount: p.grossAmount, tds: p.tds, advance: p.advance, hold: p.hold, netPayable: p.netPayable, remarks: p.remarks, location: p.location, user: p.user, checker: p.checker, authorizer: p.authorizer, rejecter: p.rejecter, vid: p.vendorId }));
    const vendorPayments = paymentData.reduce((vendor, payment) => {
      vendor[payment.vid] = (vendor[payment.vid] || 0) + payment.netPayable;
      return vendor;
    }, {});
    const vendorData = vendors.filter(vendor => vendorPayments[vendor.id])
      .map(vendor => ({
        name: vendor.name,
        acNo: vendor.acNo,
        ifsc: vendor.ifsc,
        bankName: vendor.bankName,
        pan: vendor.pan,
        gstin: vendor.gstin,
        phone: vendor.phone,
        email: vendor.email,
        amount: vendorPayments[vendor.id]
      }));

    let wb = XLSX.utils.book_new(),
      pws = XLSX.utils.json_to_sheet([]),
      vws = XLSX.utils.json_to_sheet([]);

    paymentData.push({
      grossAmount: paymentData.reduce((n, { grossAmount }) => n + grossAmount, 0),
      tds: paymentData.reduce((n, { tds }) => n + tds, 0),
      advance: paymentData.reduce((n, { advance }) => n + advance, 0),
      hold: paymentData.reduce((n, { hold }) => n + hold, 0),
      netPayable: paymentData.reduce((n, { netPayable }) => n + netPayable, 0)
    })

    XLSX.utils.sheet_add_aoa(pws, paymentHeading);
    XLSX.utils.sheet_add_json(pws, preprocessData(paymentData, excludedColumns), { origin: 'A2', skipHeader: true });

    XLSX.utils.sheet_add_aoa(vws, vendorHeading);
    XLSX.utils.sheet_add_json(vws, vendorData, { origin: 'A2', skipHeader: true });

    XLSX.utils.book_append_sheet(wb, pws, "Payments");
    XLSX.utils.book_append_sheet(wb, vws, "Vendors");

    XLSX.writeFile(wb, "Report.xlsx");
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const parseDate = (dateString) => {
    const [datePart, timePart] = dateString.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  const [filteredRow, visibleRows] = React.useMemo(
    () => {
      //stableSort(rows, getComparator(order, orderBy))
      //console.log('inside memo');
      const isNumeric = headCells.find(head => head.id == orderBy);

      // any pending checked authrizwd rejected
      const statusIndex = paymentStatusList.findIndex(item => item.name == paymentStatus);
      const locationIndex = locationList.findIndex(item => item.name == location)

      const filteredRows = rows.filter(row => {
        return (row.vendor.toLowerCase().includes(filterSwitch.toLowerCase())
          && (statusIndex < 1 ||
            (statusIndex == 4 && row.rejecter != null) ||
            (statusIndex == 3 && row.authorizer != null) ||
            (statusIndex == 2 && row.checker != null && row.authorizer == null && row.rejecter == null) ||
            (statusIndex == 1 && row.checker == null && row.rejecter == null))
          && (locationIndex < 1 ||
            row.location == location
          )
          && (reqStartDate == null || (row.createdOn != null && parseDate(row.createdOn) >= reqStartDate))
          && (reqEndDate == null || (row.createdOn != null && parseDate(row.createdOn) <= reqEndDate))
          && (checkStartDate == null || (row.checkedOn != null && parseDate(row.checkedOn) >= checkStartDate))
          && (checkEndDate == null || (row.checkedOn != null && parseDate(row.checkedOn) <= checkEndDate))
          && (authStartDate == null || (row.authorizedOn != null && parseDate(row.authorizedOn) >= authStartDate))
          && (authEndDate == null || (row.authorizedOn != null && parseDate(row.authorizedOn) <= authStartDate))
          && (rejStartDate == null || (row.rejectedOn != null && parseDate(row.rejectedOn) >= rejStartDate))
          && (rejEndDate == null || (row.rejectedOn != null && parseDate(row.rejectedOn) <= rejEndDate))
        )
      }).slice().sort(getComparator(order, orderBy, isNumeric));

      const start = Math.max(Math.min(page * rowsPerPage, filteredRows.length - 1), 0);
      const end = start + rowsPerPage;

      if (page > filteredRows.length / rowsPerPage)
        setPage(Math.round(filteredRows.length / rowsPerPage - 1))
      if (page < 0)
        setPage(0)

      return [filteredRows, filteredRows.slice(
        start,
        end,
      )];
    },
    [rows, filterSwitch, order, orderBy, page, rowsPerPage, paymentStatus, location, reqStartDate, reqEndDate, checkStartDate, checkEndDate, authStartDate, authEndDate, rejStartDate, rejEndDate],
  );

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <FilterDrawer setFilterSwitch={setFilterSwitch}
            paymentStatusList={paymentStatusList} setPaymentStatus={setPaymentStatus}
            locationList={locationList} setLocation={setLocation}
            setReqSDate={setReqStartDate} setReqEDate={setReqEndDate}
            setCheSDate={setCheckStartDate} setCheEDate={setCheckEndDate}
            setAuthSDate={setAuthStartDate} setAuthEDate={setAuthEndDate}
            setRejSDate={setRejStartDate} setRejEDate={setRejEndDate}
          />
          {/* <EnhancedTableToolbar numSelected={selected.length} />  */}

          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? 'small' : 'medium'}
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
                {visibleRows.map((row) => (
                  <Row key={row.id} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRow.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
        <Fab style={{
          position: 'fixed',
          bottom: '2rem',
          right: '6rem', // Adjust 240px to the width of the drawer
          transition: 'right 0.2s ease',
        }} onClick={handleDownload} color="success" aria-label="close">
          <Download />
        </Fab>
      </Box>
      <br />
      <br />
    </>

  );
}


